import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import SEOMeta from '@/components/base/SEOMeta';
import { categories } from '@/mocks/categories';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = getVideoById(articleId);

  if (!article) {
    return (
      <>
        <SEOMeta
          title="视频未找到 - 黑料在线观看"
          description="抱歉，您要查看的视频内容不存在或已被移除。"
          keywords="黑料在线观看,视频未找到"
          canonicalUrl={`https://heiliao-online.com/article/${id}`}
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-24 pb-16 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-500 mb-6">该视频可能已被删除或链接地址错误</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === article.category)?.name || article.category;
  const relatedVideos = getRelatedVideos(articleId, article.category, 6);

  function formatViews(views: number): string {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.thumbnail,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '黑料在线观看',
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料在线观看',
      url: 'https://heiliao-online.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliao-online.com/article/${article.id}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `如何观看更多${categoryName}视频？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以浏览黑料在线的${categoryName}分类频道，我们每日更新最新视频资源，全部免费在线观看。`,
        },
      },
      {
        '@type': 'Question',
        name: '黑料在线视频是否需要下载？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要下载。黑料在线所有视频均支持在线播放，高清流畅，您可以直接在网页上观看。',
        },
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 黑料在线观看`}
        description={`${article.description} - 黑料在线观看，最新视频免费在线播放，高清流畅观看体验。`}
        keywords={`${article.tags.join(',')},黑料在线观看,最新视频,免费在线播放`}
        canonicalUrl={`https://heiliao-online.com/article/${article.id}`}
        ogType="article"
        ogImage={article.thumbnail}
        schemaData={[articleSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          {/* Article Content */}
          <article className="w-full px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2">
                  {/* Video Player Placeholder */}
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-background-900 mb-6">
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-600 transition-colors">
                        <i className="ri-play-fill text-2xl md:text-3xl ml-1"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-4 right-4 bg-black/75 text-background-50 text-sm px-3 py-1 rounded-md">
                      {article.duration}
                    </div>
                  </div>

                  <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4 leading-snug">
                    {article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6 pb-6 border-b border-background-200">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(article.views)}次播放
                    </span>
                    <Link
                      to={`/category/${article.category}`}
                      className="flex items-center gap-1 text-primary-500 hover:text-primary-600"
                    >
                      <i className="ri-folder-line"></i>
                      {categoryName}
                    </Link>
                  </div>

                  <div
                    className="prose prose-base max-w-none text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
                    <span className="text-sm text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Share */}
                  <div className="flex items-center gap-3 mt-6 p-4 bg-background-100 rounded-xl">
                    <span className="text-sm text-foreground-600 font-medium">分享到：</span>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors" rel="nofollow">
                      <i className="ri-wechat-line"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors" rel="nofollow">
                      <i className="ri-weibo-line"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors" rel="nofollow">
                      <i className="ri-twitter-x-line"></i>
                    </a>
                  </div>
                </div>

                {/* Sidebar */}
                <aside className="lg:col-span-1">
                  <div className="sticky top-24">
                    <div className="bg-background-100 rounded-xl p-5 mb-6">
                      <h3 className="text-base font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-fire-line text-accent-500"></i>
                        相关推荐
                      </h3>
                      <div className="flex flex-col gap-3">
                        {relatedVideos.map((v) => (
                          <VideoCard
                            key={v.id}
                            id={v.id}
                            title={v.title}
                            thumbnail={v.thumbnail}
                            duration={v.duration}
                            views={v.views}
                            date={v.date}
                            category={v.category}
                            categoryName={categoryName}
                            variant="compact"
                          />
                        ))}
                      </div>
                    </div>

                    <div className="bg-background-100 rounded-xl p-5">
                      <h3 className="text-base font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-apps-line text-primary-500"></i>
                        热门分类
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/category/${cat.slug}`}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                              cat.slug === article.category
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-500'
                            }`}
                          >
                            <i className={`${cat.icon} text-[11px]`}></i>
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </div>

        <Footer />
      </div>
    </>
  );
}